import { useId } from "react";

import { Span, Tooltip } from "@virtual-brain-lib/frontend-design-system/ui";
import { cn } from "@virtual-brain-lib/frontend-design-system/utils";
import type { AdminAnalyticsHeatmapCell as HeatmapCell } from "@virtual-brain-lib/types";

import { AnalyticsCard } from "../primitives/AnalyticsCard";
import { EmptyState } from "../primitives/EmptyState";
import { Skeleton } from "../primitives/Skeleton";
import { formatNumber } from "../utils/format";
import { HEATMAP_CLASSES, heatmapClass } from "../utils/palette";

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** One weekday × hour square; hovering an active cell shows its exact count. */
function HeatmapSquare({
	value,
	max,
	label,
	hour,
}: {
	readonly value: number;
	readonly max: number;
	readonly label: string;
	readonly hour: number;
}) {
	const tooltipId = useId();
	const hasValue = value > 0;

	return (
		<>
			<div
				data-tooltip={hasValue ? tooltipId : undefined}
				className={cn("h-5 flex-1 rounded-sm", heatmapClass(value, max))}
			/>
			{hasValue && (
				<Tooltip anchor={tooltipId} size="xs">
					{`${label} ${hour}:00 - ${formatNumber(value)}`}
				</Tooltip>
			)}
		</>
	);
}

/** Day-of-week × hour-of-day heatmap (from the Helpline dashboard). */
type UsageHeatmapProps = {
	readonly cells: HeatmapCell[];
	readonly isLoading?: boolean;
	readonly className?: string;
};

function HeatmapScale() {
	return (
		<div className="flex items-center gap-1">
			<Span variant="body-2xs" color="defaultLight">
				Less
			</Span>
			{HEATMAP_CLASSES.map((swatch, index) => (
				<span key={index} className={cn("size-3 rounded-sm", swatch)} />
			))}
			<Span variant="body-2xs" color="defaultLight">
				More
			</Span>
		</div>
	);
}

export function UsageHeatmap({ cells, isLoading = false, className }: UsageHeatmapProps) {
	const max = cells.reduce((acc, cell) => Math.max(acc, cell.value), 0);
	const isEmpty = max === 0;

	// The backend buckets by UTC weekday/hour; shift each cell into the viewer's local time.
	const offsetHours = Math.round(-new Date().getTimezoneOffset() / 60);
	const valueAt = new Map<string, number>();

	for (const cell of cells) {
		const totalHour = cell.hour + offsetHours;
		const hour = ((totalHour % 24) + 24) % 24;
		const day = (((cell.day + Math.floor(totalHour / 24)) % 7) + 7) % 7;

		valueAt.set(`${day}-${hour}`, cell.value);
	}

	return (
		<AnalyticsCard
			title="Usage heatmap"
			subtitle="Chat messages + App sessions combined, by weekday and hour (local time)"
			icon="clock"
			actions={!isLoading && !isEmpty ? <HeatmapScale /> : undefined}
			className={className}
		>
			{isLoading && (
				<div className="flex flex-col gap-1">
					{Array.from({ length: 7 }).map((_, row) => (
						<Skeleton key={row} className="h-5 w-full" />
					))}
				</div>
			)}

			{!isLoading && isEmpty && (
				<EmptyState
					icon="clock"
					title="No activity in this period"
					message="Usage patterns will appear once your team is active."
				/>
			)}

			{!isLoading && !isEmpty && (
				<div className="overflow-x-auto">
					<div className="flex min-w-160 flex-col gap-1">
						{/* Hours top row */}
						<div className="flex items-center gap-0.5">
							<span className="w-9 shrink-0" />
							{HOURS.map((hour) => (
								<span key={hour} className="flex-1 text-center">
									<Span variant="body-2xs" color="defaultLight">
										{hour % 3 === 0 ? hour : ""}
									</Span>
								</span>
							))}
						</div>

						{/* Weekdays left side */}
						{WEEKDAY_LABELS.map((label, day) => (
							<div key={label} className="flex items-center gap-0.5">
								<span className="w-9 shrink-0">
									<Span variant="body-2xs" color="defaultLight">
										{label}
									</Span>
								</span>

								{/* Heatmap cell for each hour */}
								{HOURS.map((hour) => (
									<HeatmapSquare
										key={hour}
										value={valueAt.get(`${day}-${hour}`) ?? 0}
										max={max}
										label={label}
										hour={hour}
									/>
								))}
							</div>
						))}
					</div>
				</div>
			)}
		</AnalyticsCard>
	);
}
